import React, { useState, useEffect, useContext, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Icon, Dropdown } from 'semantic-ui-react';
import { useSelector, shallowEqual } from 'react-redux';

import { CopilotContext, CopilotProvider } from '../CopilotContext';
import ROI, { buildRoiAnalysisPayload } from '../Functions/ROI';
import { formatElements } from '../Functions/parseText';
import AICard from '../Components/ROICard';
import Suggestions from '../Components/SuggestionsOutcomes';
import FollowUpCard from '../Components/FollowUpCard';
import { RunThematicOutcome } from '../Functions/ThematicsAnalysis';
import ROIReport from '../Components/ROIReport';
import { RoiIcon } from '../Home';
import HistoryButton from '../Components/HistoryButton';
import { Separator } from 'new_components/templates/ZeroStateScreen';
import { usePageTranslations } from 'hooks/usePageTranslations';
// import { useDataSelectionGuard } from '../../NewAnalytics/DataSelectionGuard';

function ROIHomeComponent({ goHome, initialSession, onSaveSession, openHistory, hasHistory = true }) {
  const t = usePageTranslations('ProtoType/CopilotV2/Workflows/ROIHome/translations');
  const scrollRef = useRef(null);
  const endOfMessagesRef = useRef(null);

  // Guard to ensure data is selected, redirects to NewAnalytics if not
  // const { hasData } = useDataSelectionGuard();

  const core_data = useSelector((state) => state.audit?.core_data, shallowEqual);
  const raw_data = useSelector((state) => state.audit?.raw_data, shallowEqual);
  const getOrganizations = useSelector((state) => state.organizations);
  const getAuth = useSelector((state) => state.auth);
  const getSurveyQuestions = useSelector((state) => state.surveyquestions);

  const {
    messages,
    setMessages,
    phase,
    clearMessages,
    setPromptText,
    promptText,
    executeProcess,
    callOpenAIJSON,
  } = useContext(CopilotContext);

  const [started, setStarted] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [promptKey, setPromptKey] = useState(0);
  const [suggestionHistory, setSuggestionHistory] = useState([]);
  const [followUp, setFollowUp] = useState(false);
  const [selectedFactors, setSelectedFactors] = useState("");
  const [factorDropdown, setFactorDropdown] = useState([]);

  // For showing/hiding the Scroll-to-bottom button
  const [showScrollButton, setShowScrollButton] = useState(false);

  /**
   * 1) Load initialSession if provided
   */
  useEffect(() => {
    if (initialSession) {
      if (initialSession.messages?.length) {
        setMessages(initialSession.messages);
        setStarted(true);
      }
      if (initialSession.suggestionHistory) {
        setSuggestionHistory(initialSession.suggestionHistory);
      }
      if(initialSession.selectedFactors) {
        setSelectedFactors(initialSession.selectedFactors);
      }

    }
  }, [initialSession, setMessages]);

  /**
   * 2) If user sets a new prompt & no existing messages, start
   */
  useEffect(() => {
    if (prompt && !started && messages.length === 0) {
      setStarted(true);
      setMessages((prev) => [
        ...prev,
        {
          role: "function",
          content: t('runningRoiAnalysis'),
          type: "ROI Analysis",
        },
      ]);
    }
  }, [prompt, promptKey, started, messages, setMessages]);

  /**
   * 3) If user triggers a follow-up, add that once
   */
  useEffect(() => {
    if (followUp) {
      setMessages((prev) => [
        ...prev,
        { role: "follow_up", content: t('runningThematicAnalysisOnFeedback') },
      ]);
    }
  }, [followUp, setMessages]);

  /**
   * 4) Set up factor dropdown options
   */
  useEffect(() => {
    const factorDrop = [];
    const orgId = getAuth?.organization_id || getOrganizations?.organization?.id;
    const organizationData = getOrganizations?.[orgId];
    if (organizationData) {
      const sortOrder = organizationData?.styling?.survey_sequence?.find(
        (item) => item.value === "outcome_question"
      )?.question?.sort_order;

      const outcomeQ = getSurveyQuestions?.outcome_questions?.find(
        (item) => item.sort_order === sortOrder
      );

      // Add outcome factors - include all outcome questions
      outcomeQ?.questions?.forEach((q) => {
        factorDrop.push({
          text: q.name,
          value: { ...q, type: "outcome" },
        });
      });
    }

    setFactorDropdown(factorDrop);
    if (!selectedFactors && factorDrop[0]?.value) {
      setSelectedFactors(factorDrop[0]?.value);
    }
  }, [core_data, getAuth, getOrganizations, getSurveyQuestions]);

  /**
   * 5) (REMOVED auto-scroll)
   *    Instead, we attach a scroll listener to show/hide the button.
   */
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;
      const { scrollTop, clientHeight, scrollHeight } = scrollRef.current;
      const threshold = 100; // px from bottom to hide button

      if (scrollHeight - scrollTop - clientHeight < threshold) {
        setShowScrollButton(false);
      } else {
        setShowScrollButton(true);
      }
    };

    const container = scrollRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    // Cleanup listener on unmount
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [scrollRef]);

  /**
   * 5) Called when user picks new data/prompt from <ROI>
   */
  const registerHistory = (data, promptString, analysisBreakdown, outcome) => {
    setPrompt(promptString);
    setPromptKey((prev) => prev + 1);
    setSuggestionHistory((prev) => [...prev, { data, analysisBreakdown, outcome }]);
  };

  /**
   * 6) Single callback at end of streaming
   */
  const handleStreamingDone = (finalMessages) => {
    onSaveSession?.({
      type: "roi",
      messages: finalMessages,
      suggestionHistory,
      selectedFactors,
    });
  };

  /**
   * 7) goBack => reset everything
   */
  const goBack = () => {
    clearMessages();
    setPromptText("");
    setPrompt("");
    setStarted(false);
  };

  /**
   * 8) If not started and we have no messages, show "intro"
   */
  // Don't render until data check completes
  // if (!hasData) {
  //   return null;
  // }

  if (!started && messages.length === 0) {
    return (
      <Container>
        {/* <BackButton onClick={goHome}>
          <Icon style={{ marginRight: 10 }} name="arrow left" />
          Back
        </BackButton> */}
        <ROI
          intro={true}
          onClick={({ data, prompt, organized_feedback, outcome }) =>
            registerHistory(data, prompt, organized_feedback, outcome)
          }
          selectedFactors={selectedFactors}
          setSelectedFactors={setSelectedFactors}
          openHistory={openHistory}
          hasHistory={hasHistory}
        />
      </Container>
    );
  }
  // Handler for regenerating analysis
  const handleRegenerateAnalysis = () => {
    clearMessages();
    setFollowUp(false);

    const { hasRoiData: canRun, promptPayload } = buildRoiAnalysisPayload({
      raw_data,
      core_data,
      selectedFactors,
    });

    if (!canRun || !promptPayload) {
      setStarted(false);
      setPrompt("");
      setPromptKey(0);
      setSuggestionHistory([]);
      return;
    }

    setSuggestionHistory([
      {
        data: promptPayload.data,
        analysisBreakdown: promptPayload.organized_feedback,
        outcome: promptPayload.outcome,
      },
    ]);
    setStarted(false);
    setPrompt(promptPayload.prompt);
    setPromptKey((prev) => prev + 1);
  };

  return (
    <Container ref={scrollRef}>
      {/* <BackButton onClick={goBack}>
        <Icon style={{ marginRight: 10 }} name="arrow left" />
        Back
      </BackButton> */}
      <HeaderRow>
        <PageTitle>
          <TitleIconWrapper>
            <RoiIcon />
          </TitleIconWrapper>
          {t('roiAnalysis')}
        </PageTitle>
        <ActionControls>
          <Dropdown
            selection
            placeholder={t('selectOutcome')}
            value={selectedFactors}
            options={factorDropdown}
            onChange={(e, { value }) => setSelectedFactors(value)}
            style={{ minWidth: '200px' }}
          />
          <RegenerateButton onClick={handleRegenerateAnalysis}>
            {t('runNewAnalysis')}
          </RegenerateButton>
          <Separator />
          <HistoryButton onClick={openHistory} disabled={!hasHistory} />
        </ActionControls>
      </HeaderRow>

      <Content>
        <MessageContainer>
          {messages.map((msg, i) => {
            // ---- If it's a function message -> <AICard> ----
            if (msg.role === "function") {
              // Check if there's an assistant message AFTER this function
              const hasAssistantAfter = messages
                .slice(i + 1)
                .some((m) => m.role === "assistant");

              return (
                <AICard
                  key={i}
                  alreadyRun={hasAssistantAfter}
                  prompt={prompt}
                  start={() => callOpenAIJSON(prompt)}
                  onDone={(finalMessages) => {
                    onSaveSession?.({
                      type: "roi",
                      messages: finalMessages,
                      selectedFactors,
                      suggestionHistory,
                    });
                  }}
                />
              );
            }

            // ---- If it's a follow_up -> <FollowUpCard> ----
            if (msg.role === "follow_up") {
              return (
                <FollowUpCard
                  key={i}
                  start={() => {
                    RunThematicOutcome(
                      (args, cb) => executeProcess(args, cb),
                      followUp,
                      { onDone: handleStreamingDone }
                    );
                  }}
                />
              );
            }

            // ---- Otherwise (assistant/user) ----
            // Count how many ROI Analysis function messages came before this one
            // to determine which suggestionHistory entry corresponds to this report
            const roiAnalysisIndex = messages
              .slice(0, i)
              .filter((m) => m.role === "function" && m.type === "ROI Analysis")
              .length - 1;

            const retrievedSuggestion = roiAnalysisIndex >= 0
              ? suggestionHistory[roiAnalysisIndex]
              : suggestionHistory[suggestionHistory.length - 1];
            const currentlyRisk = messages[i - 1]?.type === "ROI Analysis";

            return (
              <Bubble key={i} role={msg.role}>
                {currentlyRisk && (
                  <ROIReport
                    data={retrievedSuggestion}
                    type={retrievedSuggestion?.outcome || selectedFactors}
                    content={msg.content}
                  />
                )}
                {!currentlyRisk && formatElements(msg.content)}
                {phase === "idle" &&
                  i + 1 === messages.length &&
                  retrievedSuggestion && (
                    <Suggestions
                      data={retrievedSuggestion}
                      selectFollowUp={(d) =>
                        setFollowUp({ data: retrievedSuggestion, selection: d })
                      }
                    />
                  )}
              </Bubble>
            );
          })}
          <div ref={endOfMessagesRef} />
        </MessageContainer>
        {/* 
        <SettingsArea>
          <ROI
            intro={false}
            onClick={({ data, prompt, organized_feedback }) => registerHistory(data, prompt, organized_feedback)}
          />
        </SettingsArea> */}
        {/* Show the scroll-to-bottom button only when needed */}
      </Content>
    </Container>
  );
}

export default function ROIHome(props) {
  return (
    <CopilotProvider>
      <ROIHomeComponent {...props} />
    </CopilotProvider>
  );
}

// ------------------------ Styles ------------------------
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  font-family: "Raleway", sans-serif;
  padding: 20px;
  padding-top: 0px;
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 30px;
`;

const PageTitle = styled.div`
  font-size: 28px;
  font-weight: bold;
  color: #2A3039;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #F8F9FA;
  border-radius: 7px;
  color: #1F2937;
`;

const ActionControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  > * {
    display: flex;
    align-items: center;
    margin: 0 !important;
  }
  
  button {
    margin: 0 !important;
  }
`;

const RegenerateButton = styled.button`
  padding: 10px 20px;
  background-color: #2d70e2;
  color: white;
  cursor: pointer;
  transition: 0.3s;
  border-radius: 8px;
  display: inline-block;
  font-size: 14px;
  font-weight: 500;
  border: none;
  font-family: "Raleway";

  &:hover {
    background-color: #1c4db8;
  }
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #2a3039;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 20px;
  width: 100%;
`;

const MessageContainer = styled.div`
  width: 100%;
  padding-bottom: 150px;
`;

const Bubble = styled.div`
  background-color: ${(props) =>
    props.role === "assistant" ? "white" : "#dcecff"};
  margin-bottom: 8px;
  border-radius: 6px;
  max-width: 100%;
  align-self: ${(props) =>
    props.role === "assistant" ? "flex-start" : "flex-end"};
  animation: ${fadeIn} 0.5s forwards;
`;
